import styled from "@emotion/styled";
import { Box, Alert, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button as MuiButton, CircularProgress, Typography, FormControl, Select, MenuItem } from "@mui/material";
import { useMutation, useQuery, keepPreviousData } from "@tanstack/react-query";
import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import {
  deletewithQueryApiServices,
  getWithQueryApiServices,
  postWithQueryApiServices,
} from "../../../apis/api";
import { apiRoutes } from "../../../apis/apiPath";
import { companyFormInitVal, expirationValues } from "../../../constants";
import { SplitContainer, StyledHeader } from "../../../styles/commonStyles";
import { errorToast, successToast } from "../../../utils/helper";
import { FullFeaturedCrudGrid } from "../../shared/CommonTable";
import { CustomSelectField } from "../../shared/CustomSelectField";
import { ParentPageWrapper } from "../../shared/ParentPageWrapper";
import { CategoryForm } from "./CategoryForm";
import { CategoryUser } from "./CategoryUser";
import { useTenantGuard } from "../../../hooks/useTenantGuard";
import { useUserRole } from "../../../hooks/useUserRole";
import { usePermissions } from "../../../hooks/usePermissions";
import { PermissionDenied } from "../../shared/PermissionDenied";

const BoxContainer = styled(Box)(({ theme }) => ({
  padding: "10px",
  [theme.breakpoints.up("sm")]: {
    padding: "24px",
  },
  [theme.breakpoints.up("md")]: {
    padding: "1%",
  },
  boxSizing: "border-box",
}));

const ParentContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  flexWrap: "wrap",
  gap: "20px",
  [theme.breakpoints.down("lg")]: {
    flexDirection: "column",
    alignItems: "stretch",
    gap: "30px",
  },
}));

const ResponsiveSplitContainer = styled(SplitContainer)(({ theme }) => ({
  [theme.breakpoints.down("lg")]: {
    width: "100% !important",
  },
}));

const DisabledPaneOverlay = styled("div")(({ disabled }) => ({
  position: "relative",
  height: "100%",
  transition: "all 0.3s ease",
  pointerEvents: "auto",
  "&::after": disabled ? {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.01)",
    pointerEvents: "none",
    zIndex: 1,
    borderRadius: 10,
  } : {},
}));

export const Category = () => {
  const [companyForm, setCompanyForm] = useState([]);
  const [searchCategory, setSearchCategory] = useState("");
  const [debouncedSearchCategory, setDebouncedSearchCategory] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchCategory(searchCategory);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchCategory]);

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categoryData, setCategoryData] = useState({});
  const [addNewCategory, setAddNewCategory] = useState(false);
  const [companyError, setCompanyError] = useState(false);

  const [originalCompaniesWithCategory, setOriginalCompaniesWithCategory] = useState([]);
  const [pendingSelectCategoryId, setPendingSelectCategoryId] = useState(null);

  const [isEditingLeftPane, setIsEditingLeftPane] = useState(false);
  const [isEditingCenterPane, setIsEditingCenterPane] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [copySuccessTrigger, setCopySuccessTrigger] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [companyPaginationModel, setCompanyPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    pendingAction: null,
    pendingData: null,
  });

  const { isTenantBlocked, tenantMessage } = useTenantGuard();
  const { isCurrentUserSysAdmin, isUserInCompany, isGlobalSuperAdmin, isLoading: roleLoading } = useUserRole();
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();

  const canCreateCat = hasPermission("Categories: Create");
  const canUpdateCat = hasPermission("Categories: Update");
  const canDeleteCat = hasPermission("Categories: Delete");
  const canReadCat = hasPermission("Categories: Read");
  const canUpdateMetadata = hasPermission("Edit Metadata") || hasPermission("Criteria: Update");

  const { currentCompany, tenantString, setCurrentCompany, fullCompanyList, isCompanySwitching } = useOutletContext();
  const [selectedCompanyId, setSelectedCompanyId] = useState(currentCompany || "");

  // Sync selectedCompanyId when currentCompany changes initially
  useEffect(() => {
    if (currentCompany) {
      setSelectedCompanyId(currentCompany);
    }
  }, [currentCompany]);

  // Reset page to 0 when search filter or company changes
  useEffect(() => {
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, [searchCategory, selectedCompanyId]);

  const { data: categotyData, refetch: categoryListRefetch, isFetching: categoryListFetching } = useQuery({
    queryKey: ["categoryLists", selectedCompanyId, debouncedSearchCategory, tenantString, paginationModel],
    queryFn: () =>
      getWithQueryApiServices(
        apiRoutes.GET_CATEGOTY_LIST,
        `pageNumber=${paginationModel.page + 1}&pageSize=25&selectedCompany=${selectedCompanyId}&search=${debouncedSearchCategory}${tenantString}`
      ),
    enabled: !!selectedCompanyId,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (pendingSelectCategoryId && categotyData?.data?.list) {
      const newCat = categotyData.data.list.find(
        (cat) => cat.categoryID === pendingSelectCategoryId
      );
      if (newCat) {
        const selectedRow = {
          ...newCat,
          id: newCat.categoryID
        };
        setSelectedCategories([selectedRow]);
        setCategoryData(selectedRow);
        if (!isTenantBlocked) {
          setAddNewCategory(true);
        }
        setPendingSelectCategoryId(null);
      }
    }
  }, [categotyData, pendingSelectCategoryId, isTenantBlocked]);

  const handleGridSuccess = (dataOrRow) => {
    if (dataOrRow && dataOrRow.insertId) {
      setPendingSelectCategoryId(dataOrRow.insertId);
    } else if (dataOrRow) {
      const updatedRow = {
        ...dataOrRow,
        id: dataOrRow.id || dataOrRow.categoryID
      };
      setSelectedCategories([updatedRow]);
      setCategoryData(updatedRow);
    }
  };

  const handleGlobalCompanyChange = (e) => {
    if (setCurrentCompany) setCurrentCompany(e.target.value);
  };

  const isLeftPaneDisabled = isEditingCenterPane;
  const isCenterPaneDisabled = isEditingLeftPane || (!canUpdateMetadata && !isGlobalSuperAdmin);
  const isRightPaneDisabled = isEditingLeftPane || isEditingCenterPane || !canUpdateCat;

  const columns = [
    { 
      field: "name", 
      headerName: "Name", 
      flex: 1, 
      required: true,
      editable: !isTenantBlocked && !isLeftPaneDisabled && canUpdateCat
    },
    {
      field: "expiration",
      headerName: "Expiration",
      align: "left",
      headerAlign: "left",
      editable: !isTenantBlocked && !isLeftPaneDisabled && canUpdateCat,
      required: true,
      flex: 0.5,
      type: "singleSelect",
      valueGetter: (params) => {
        const selected = expirationValues?.find(
          (option) => option.value == params
        );
        return selected?.value || "";
      },
      valueOptions: expirationValues,
      getOptionValue: (value) => value.value,
      getOptionLabel: (value) => value.name,
    },
  ];

  const handleConfirmDialogClose = () => {
    setConfirmDialog({ open: false, pendingAction: null, pendingData: null });
  };

  const handleConfirmDiscard = () => {
    setHasUnsavedChanges(false);
    setIsEditingCenterPane(false);
    
    if (confirmDialog.pendingAction === 'selectCompany') {
      executeCompanySelection(confirmDialog.pendingData);
    } else if (confirmDialog.pendingAction === 'selectCategory') {
      executeCategorySelection(confirmDialog.pendingData);
    }
    
    handleConfirmDialogClose();
  };

  const executeCompanySelection = (val) => {
    let copyValue = [...companyForm];
    if (copyValue?.some((item) => String(item?.companyID) === String(val?.companyID))) {
      copyValue = copyValue?.filter(
        (item) => String(item?.companyID) !== String(val?.companyID)
      );
    } else {
      copyValue.push(val);
    }
    setCompanyForm(copyValue);
  };

  const executeCategorySelection = (item) => {
    let copyValue = [...selectedCategories];
    if (copyValue.some((cat) => cat?.id === item?.id)) {
      copyValue = copyValue.filter((val) => val?.id !== item?.id);
    } else {
      copyValue = [item];
    }
    setSelectedCategories([...copyValue]);

    if (copyValue?.length === 1 && !copyValue[0].isNew) {
      setCategoryData(copyValue?.[0]);
      if (!isTenantBlocked) {
        setAddNewCategory(true);
      }
    } else {
      setCategoryData({});
      setAddNewCategory(false);
    }
  };

  const handleCompanySelection = (val) => {
    if (hasUnsavedChanges) {
      setConfirmDialog({
        open: true,
        pendingAction: 'selectCompany',
        pendingData: val,
        confirm: true,
      });
      return;
    }
    executeCompanySelection(val);
  };

  const handleCategoryCheckbox = (item) => {
    if (selectedCategories?.some((cat) => cat?.id === item?.id)) {
      return;
    }
    if (hasUnsavedChanges) {
      setConfirmDialog({
        open: true,
        pendingAction: 'selectCategory',
        pendingData: item,
        confirm: true,
      });
      return;
    }
    executeCategorySelection(item);
  };

  // ✅ Compare original with current to determine ADD/REMOVE
  const submitCopyCategories = () => {
    if (isTenantBlocked) {
      errorToast(tenantMessage);
      return;
    }

    if (!selectedCategories || selectedCategories.length === 0) {
      errorToast("Please select a category first");
      return;
    }

    // Get currently selected company IDs
    const selectedCompanyIds = companyForm?.map((item) => String(item?.companyID)) || [];
    
    // Get original company IDs that had the category
    const originalCompanyIds = originalCompaniesWithCategory.map(c => String(c.companyID));

    // Companies to ADD (newly checked, not in original)
    const companiesToAdd = companyForm
      .filter(item => !originalCompanyIds.includes(String(item.companyID)))
      .map(item => item.companyID);

    // Companies to REMOVE (were in original but now unchecked AND have no data)
    const companiesToRemove = originalCompaniesWithCategory
      .filter(orig => !selectedCompanyIds.includes(String(orig.companyID)) && !orig.hasData)
      .map(c => c.companyID);

    // Check if there's anything to do
    if (companiesToAdd.length === 0 && companiesToRemove.length === 0) {
      errorToast("No changes to apply");
      return;
    }

    let workData = {
      companiesToAdd,
      companiesToRemove,
      categories: selectedCategories?.map((item) => ({
        name: item?.name,
        expiration: item?.expiration,
      })),
    };

    updateCopyCategories.mutate(workData);
  };

  const { data: companyData, refetch: companyListRefetch, isFetching: isCompanyFetching } = useQuery({
    queryKey: ["companyLists", tenantString, companyPaginationModel],
    queryFn: () =>
      getWithQueryApiServices(
        apiRoutes.GET_COMPANY_LIST,
        `pageNumber=${companyPaginationModel.page + 1}&pageSize=25${tenantString}`
      ),
    placeholderData: keepPreviousData,
  });


  const deleteCategory = useMutation({
    mutationKey: ["deleteCategoryDetails"],
    mutationFn: (categoryID) =>
      deletewithQueryApiServices(
        apiRoutes.DELETE_CATEGORY,
        `selectedCompany=${selectedCompanyId}${tenantString}`,
        categoryID
      ),
    onSuccess: () => {
      categoryListRefetch();
      clearAllSelections();
      successToast("Category Deleted!");
    },
    onError: (error) => {
      if (error?.response?.data?.code !== 'TENANT_NOT_ACTIVE') {
        errorToast(error?.response?.data?.message || "Failed to delete category");
      }
    }
  });

  const updateCopyCategories = useMutation({
    mutationKey: ["updateCopyCategories"],
    mutationFn: (workData) =>
      postWithQueryApiServices(
        apiRoutes.COPY_CATEGORIES,
        `selectedCompany=${selectedCompanyId}${tenantString}`,
        workData
      ),
    onSuccess: () => {
      categoryListRefetch();
      successToast("Categories updated successfully");
      
      // ✅ Trigger refetch (will update selection and badges)
      setCopySuccessTrigger(prev => prev + 1);
    },
    onError: (error) => {
      if (error?.response?.data?.code !== 'TENANT_NOT_ACTIVE') {
        errorToast(error?.response?.data?.message || "Failed to update categories");
      }
    }
  });

  const clearAllSelections = useCallback(() => {
    setSelectedCategories([]);
    setCategoryData({});
    setAddNewCategory(false);
    setCompanyForm([]);
    setOriginalCompaniesWithCategory([]);
    setHasUnsavedChanges(false);
    setIsEditingLeftPane(false);
    setIsEditingCenterPane(false);
  }, []);

  const handleLeftPaneCancel = useCallback(() => {
    setSelectedCategories([]);
    setCategoryData({});
    setAddNewCategory(false);
    setIsEditingLeftPane(false);
  }, []);

  const handleCenterPaneComplete = useCallback(() => {
    setSelectedCategories([]);
    setCategoryData({});
    setAddNewCategory(false);
    setHasUnsavedChanges(false);
    setIsEditingCenterPane(false);
  }, []);

  useEffect(() => {
    if (selectedCompanyId) {
      clearAllSelections();
      setSearchCategory("");
    }
  }, [selectedCompanyId, clearAllSelections]);

  if ((roleLoading || permissionsLoading) && !isCompanySwitching) {
    return (
      <ParentPageWrapper>
        <BoxContainer sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
          <CircularProgress />
          <Typography sx={{ ml: 2, color: "text.secondary" }}>Verifying permissions...</Typography>
        </BoxContainer>
      </ParentPageWrapper>
    );
  }
  if (!canReadCat && !isGlobalSuperAdmin) {
    return (
      <ParentPageWrapper>
        <PermissionDenied message="You do not have permission to view or manage Categories in this company." />
      </ParentPageWrapper>
    );
  }

  return (
    <ParentPageWrapper>
      <BoxContainer>
        <StyledHeader>Categories</StyledHeader>

        {isTenantBlocked && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {tenantMessage}
          </Alert>
        )}

        {(!canUpdateCat || !canCreateCat) && !isGlobalSuperAdmin && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Read-Only Mode:</strong> Some actions are restricted based on your permissions. Managing categories and mapping are limited to authorized users.
          </Alert>
        )}

        <ParentContainer>
          <ResponsiveSplitContainer style={{ backgroundColor: "transparent" }}>
            <Box sx={{ height: { xs: "80vh", lg: "80vh" } }}>
              <DisabledPaneOverlay disabled={!isUserInCompany || isLeftPaneDisabled}>
                <div
                  style={{
                    backgroundColor: (!isUserInCompany || isLeftPaneDisabled) ? "#f5f5f5" : "white",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 10,
                    overflow: "hidden",
                    opacity: isLeftPaneDisabled ? 0.7 : 1,
                    pointerEvents: isLeftPaneDisabled ? "none" : "auto",
                    cursor: isLeftPaneDisabled ? "not-allowed" : "auto",
                  }}
                >
                  <Box sx={{ p: 2, pb: 1, borderBottom: '1px solid #edf2f7' }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#1a365d', fontSize: "14px" }}>
                      Select Company
                    </Typography>
                    <FormControl size="small" fullWidth sx={{ mt: 0.5 }}>
                      <Select
                        value={selectedCompanyId || ""}
                        onChange={handleGlobalCompanyChange}
                        sx={{
                          borderRadius: '6px',
                          backgroundColor: '#f8fafc',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#cbd5e1'
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#94a3b8'
                          }
                        }}
                      >
                        <MenuItem value="" disabled>Select Company</MenuItem>
                        {fullCompanyList?.filter((company) => Number(company.dst) !== 0).map(company => (
                          <MenuItem key={company.companyID} value={company.companyID}>
                            {company.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  <div style={{ flex: 1, minHeight: 0, padding: '0 8px 8px 8px' }}>
                    <FullFeaturedCrudGrid
                      initialRows={categotyData?.data?.list}
                      columns={columns}
                      label="category"
                      handleSelection={handleCategoryCheckbox}
                      search={searchCategory}
                      setSearch={setSearchCategory}
                      apiUrl={apiRoutes.CREATE_CATEGORY}
                      refetch={categoryListRefetch}
                      initialFormValue={companyFormInitVal}
                      isTenantBlocked={isTenantBlocked}
                      tenantMessage={tenantMessage}
                      onEditingStateChange={setIsEditingLeftPane}
                      isEditing={isEditingLeftPane}
                      onCancelEdit={handleLeftPaneCancel}
                      onSuccess={handleGridSuccess}
                      selectedRowId={selectedCategories?.[0]?.categoryID || null}
                      canCreate={canCreateCat}
                      canUpdate={canUpdateCat}
                      canDelete={canDeleteCat}
                      permission={isGlobalSuperAdmin} // Legacy fallback
                      delFun={canDeleteCat || isGlobalSuperAdmin ? deleteCategory.mutate : null}
                      disabled={isLeftPaneDisabled || (!canReadCat && !isGlobalSuperAdmin)}
                      hideFooterPagination={true}
                      rowCount={categotyData?.data?.count || 0}
                      paginationModel={paginationModel}
                      onPaginationModelChange={setPaginationModel}
                      loading={categoryListFetching}
                    />
                  </div>
                </div>
              </DisabledPaneOverlay>
            </Box>
          </ResponsiveSplitContainer>

          <ResponsiveSplitContainer
            style={{
              width: "30%",
              backgroundColor: "transparent",
            }}
          >
            <DisabledPaneOverlay disabled={!isUserInCompany || isCenterPaneDisabled}>
              <CategoryForm
                categoryListRefetch={categoryListRefetch}
                companyListRefetch={companyListRefetch}
                companyForm={companyForm}
                setCompanyForm={setCompanyForm}
                categoryData={categoryData}
                setCategoryData={setCategoryData}
                setAddNewCategory={setAddNewCategory}
                addNewCategory={addNewCategory}
                setSelectedCategories={setSelectedCategories}
                isTenantBlocked={isTenantBlocked}
                tenantMessage={tenantMessage}
                hasUnsavedChanges={hasUnsavedChanges}
                setHasUnsavedChanges={setHasUnsavedChanges}
                isEditingCenterPane={isEditingCenterPane}
                setIsEditingCenterPane={setIsEditingCenterPane}
                isCenterPaneDisabled={isCenterPaneDisabled}
                onFormComplete={handleCenterPaneComplete}
              />
            </DisabledPaneOverlay>
          </ResponsiveSplitContainer>

          <ResponsiveSplitContainer
            style={{
              width: "30%",
              backgroundColor: "transparent",
            }}
          >
            <DisabledPaneOverlay disabled={!isUserInCompany || isRightPaneDisabled}>
              <CategoryUser
                companyForm={companyForm}
                setCompanyForm={setCompanyForm}
                companyData={companyData}
                isCompanyFetching={isCompanyFetching}
                setAddNewCategory={setAddNewCategory}
                addNewCategory={addNewCategory}
                setCategoryData={setCategoryData}
                categoryData={categoryData}
                selectedCategories={selectedCategories}
                companyError={companyError}
                isTenantBlocked={isTenantBlocked}
                tenantMessage={tenantMessage}
                onCompanySelect={handleCompanySelection}
                isRightPaneDisabled={isRightPaneDisabled}
                hasUnsavedChanges={hasUnsavedChanges}
                copySuccessTrigger={copySuccessTrigger}
                submitCopyCategories={submitCopyCategories}
                originalCompaniesWithCategory={originalCompaniesWithCategory}
                setOriginalCompaniesWithCategory={setOriginalCompaniesWithCategory}
                companyPaginationModel={companyPaginationModel}
                setCompanyPaginationModel={setCompanyPaginationModel}
              />
            </DisabledPaneOverlay>
          </ResponsiveSplitContainer>
        </ParentContainer>

        <Dialog
          open={confirmDialog.open}
          onClose={handleConfirmDialogClose}
          aria-labelledby="unsaved-changes-dialog-title"
          aria-describedby="unsaved-changes-dialog-description"
        >
          <DialogTitle id="unsaved-changes-dialog-title">
            Unsaved Changes
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="unsaved-changes-dialog-description">
              Click Update to save your changes or Cancel to discard.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <MuiButton onClick={handleConfirmDiscard} color="error">
              Discard Changes
            </MuiButton>
            <MuiButton onClick={handleConfirmDialogClose} variant="contained" autoFocus>
              Go Back to Edit
            </MuiButton>
          </DialogActions>
        </Dialog>
      </BoxContainer>
    </ParentPageWrapper>
  );
};